import configs from './endpoints';
import { store } from '../../../';
import { ServiceType } from '@app/types/ServiceType';

const w = globalThis as any;

const isLocalDevHost = (): boolean => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  return host === 'localhost' || host === '127.0.0.1';
};

/**
 * Agent.API is the one service that prefers the .env value while developing
 * locally: public/env-config.js is committed with deployed dev hostnames, and
 * the deployed Agent.API rejects a localhost origin, so the panel could never
 * reach a locally running instance if env-config always won.
 */
const resolveAgentApiBaseUrl = (): string | undefined => {
  const fromEnvFile = process.env.REACT_APP_BASE_URL_AGENTAPI as string | undefined;
  const fromEnvConfig = w._env_?.REACT_APP_BASE_URL_AGENTAPI as string | undefined;

  if (isLocalDevHost()) {
    // Without an explicit .env override, go through the Vite dev proxy (same
    // origin, so no CORS) instead of calling the deployed Agent.API directly.
    // The define is only injected by `vite serve`, never in builds or tests.
    const devProxy = process.env.REACT_APP_AGENTAPI_DEV_PROXY as string | undefined;
    if (!fromEnvFile && devProxy) return `${window.location.origin}${devProxy}`;
    return fromEnvFile || fromEnvConfig;
  }

  return fromEnvConfig || fromEnvFile;
};

export const baseUrls = {
  // Lazy so the host-dependent precedence above is evaluated per request rather
  // than pinned at module load, which also keeps it testable.
  get [ServiceType.AgentApi](): string | undefined {
    return resolveAgentApiBaseUrl();
  },
  [ServiceType.Autofiling]: w._env_?.REACT_APP_BASE_URL_AUTOFILING || process.env.REACT_APP_BASE_URL_AUTOFILING,
  [ServiceType.Bff]: w._env_?.REACT_APP_BASE_URL_BFF || process.env.REACT_APP_BASE_URL_BFF,
  [ServiceType.Dashboard]: w._env_?.REACT_APP_BASE_URL_DASHBOARD || process.env.REACT_APP_BASE_URL_DASHBOARD,
  [ServiceType.DocumentAdministration]: w._env_?.REACT_APP_BASE_URL_DOCUMENTADMINISTRATION || process.env.REACT_APP_BASE_URL_DOCUMENTADMINISTRATION,
  [ServiceType.FileManagement]: w._env_?.REACT_APP_BASE_URL_FILEMANAGEMENT || process.env.REACT_APP_BASE_URL_FILEMANAGEMENT,
  [ServiceType.FileStorage]: w._env_?.REACT_APP_BASE_URL_FILESTORAGE || process.env.REACT_APP_BASE_URL_FILESTORAGE,
  [ServiceType.InternalAdmin]: w._env_?.REACT_APP_BASE_URL_INTERNALADMIN || process.env.REACT_APP_BASE_URL_INTERNALADMIN,
  [ServiceType.MasterData]: w._env_?.REACT_APP_BASE_URL_MASTERDATA || process.env.REACT_APP_BASE_URL_MASTERDATA,
  [ServiceType.Notification]: w._env_?.REACT_APP_BASE_URL_NOTIFICATION || process.env.REACT_APP_BASE_URL_NOTIFICATION,
  [ServiceType.PortalAdmin]: w._env_?.REACT_APP_BASE_URL_PORTALADMIN || process.env.REACT_APP_BASE_URL_PORTALADMIN,
  [ServiceType.Portal]: w._env_?.REACT_APP_BASE_URL_PORTAL || process.env.REACT_APP_BASE_URL_PORTAL,
  [ServiceType.UserManagement]: w._env_?.REACT_APP_BASE_URL_USERMANAGEMENT || process.env.REACT_APP_BASE_URL_USERMANAGEMENT,
  [ServiceType.Wopi]: w._env_?.REACT_APP_BASE_URL_WOPI || process.env.REACT_APP_BASE_URL_WOPI,
}

/**
 * Fails loudly when the Agent.API base URL is unusable, so a misconfigured
 * environment names the offending variable instead of silently issuing the
 * request against the axios client's placeholder host.
 *
 * Deliberately agent-only: applying this to every service would turn today's
 * silent fallback into a thrown error across the app.
 */
export const assertAgentApiConfigured = (): void => {
  const url = baseUrls[ServiceType.AgentApi];

  if (!url) {
    throw new Error(
      'DM Assistant is not configured: REACT_APP_BASE_URL_AGENTAPI is not set. ' +
        'Locally, set it in ClientApp/.env.local and restart the dev server.'
    );
  }

  // An unsubstituted token would otherwise be pasted into the request path and
  // produce a confusing 404 against the frontend's own origin.
  if (url.includes('$(')) {
    throw new Error(
      `DM Assistant is not configured: REACT_APP_BASE_URL_AGENTAPI is "${url}". ` +
        'The BaseUrlAgentApi pipeline variable is missing from the Azure DevOps variable group.'
    );
  }
};

export interface ResolvedServiceUrl {
  baseURL?: string;
  url: string;
}

/**
 * Resolves a service-prefixed endpoint path (the form `endpoints.ts` produces,
 * e.g. `FileManagement/api/files`) into a base URL and a request path.
 *
 * `includeTenant` is opt-out for services that carry tenancy in the JWT rather
 * than the path — the axios client passes true, matching its long-standing
 * behaviour for every service.
 */
export const resolveServiceUrl = (
  rawUrl: string,
  { includeTenant = true }: { includeTenant?: boolean } = {}
): ResolvedServiceUrl => {
  let path: string;
  let url: string;

  try {
    path = new URL(rawUrl).pathname.slice(1);
    url = path;
  } catch {
    path = rawUrl;
    url = rawUrl;
  }

  let baseURL: string | undefined;
  const key = path.slice(0, path.indexOf('/api'));

  if (key) {
    const serviceBaseUrl = baseUrls[key];

    if (serviceBaseUrl) {
      const tenantSegment = includeTenant ? `/${store.getState().auth.tenant}` : '';
      baseURL = `${serviceBaseUrl}${tenantSegment}`;
      url = path.slice(path.indexOf('/'));
    }
  }

  const backendEnv = process.env.REACT_APP_BACKEND_ENV || w._env_?.REACT_APP_BACKEND_ENV;
  if (backendEnv) {
    baseURL = baseURL?.replace('.dev', '.' + backendEnv);
  }

  return { baseURL, url };
};

export const getApiUrl = (url: string, serviceType?: ServiceType): string => {
  if (!serviceType) return `${url}`;

  const state = store.getState();
  const { tenant } = state.auth;
  const baseURL = baseUrls[serviceType];

  if (baseURL) return `${baseURL}/${tenant}/${url}`;
  return url;
};

export default configs;
